// One headline number on the MEC dashboard. Variant set deliberately
// constrained to the warm-approachable palette tokens. Borrowed shape
// from BookkeeperDashboardPage's Metric component, with adjustments for
// MEC's information density (sub-label can be richer).

// Each tile shows a colored top stripe (using the accent-500 / -600 shade)
// for strong visual differentiation, plus a colored icon background, while
// keeping the card body in cream so the data stays readable. This was a
// deliberate amplification after first round of dashboards washed out.
const ACCENTS = {
  warm: {
    bg: 'bg-[var(--color-cream-50)]', border: 'border-[var(--color-warm-200)]',
    stripe: 'bg-[var(--color-warm-500)]', icon: 'text-[var(--color-warm-700)]',
    iconBg: 'bg-[var(--color-warm-100)]',
  },
  recovery: {
    bg: 'bg-[var(--color-recovery-50)]', border: 'border-[var(--color-recovery-300)]',
    stripe: 'bg-[var(--color-recovery-500)]', icon: 'text-white',
    iconBg: 'bg-[var(--color-recovery-500)]',
  },
  action: {
    bg: 'bg-[var(--color-action-50)]', border: 'border-[var(--color-action-300)]',
    stripe: 'bg-[var(--color-action-500)]', icon: 'text-white',
    iconBg: 'bg-[var(--color-action-500)]',
  },
  insight: {
    bg: 'bg-[var(--color-insight-50)]', border: 'border-[var(--color-insight-100)]',
    stripe: 'bg-[var(--color-insight-500)]', icon: 'text-white',
    iconBg: 'bg-[var(--color-insight-500)]',
  },
  danger: {
    bg: 'bg-[var(--color-danger-50)]', border: 'border-[var(--color-danger-300)]',
    stripe: 'bg-[var(--color-danger-500)]', icon: 'text-white',
    iconBg: 'bg-[var(--color-danger-500)]',
  },
  success: {
    bg: 'bg-[var(--color-success-50)]', border: 'border-[var(--color-success-100)]',
    stripe: 'bg-[var(--color-success-500)]', icon: 'text-white',
    iconBg: 'bg-[var(--color-success-500)]',
  },
  neutral: {
    bg: 'bg-[var(--color-cream-50)]', border: 'border-[var(--color-warm-200)]',
    stripe: 'bg-[var(--color-warm-400)]', icon: 'text-[var(--color-warm-700)]',
    iconBg: 'bg-[var(--color-warm-100)]',
  },
};

export default function MetricTile({
  label,
  value,
  sub,
  trend,            // optional: { sign: 'up'|'down', label: '...' }
  accent = 'warm',
  icon,
  loading = false,
  onClick,
  highlight = false,
}) {
  const a = ACCENTS[accent] || ACCENTS.warm;
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`
        relative overflow-hidden text-left p-5 pt-6 rounded-[var(--radius-lg)] border ${a.bg} ${a.border}
        shadow-[var(--shadow-xs)] transition-all
        ${onClick ? 'hover:shadow-[var(--shadow-md)] cursor-pointer hover:-translate-y-[1px]' : ''}
        ${highlight ? 'ring-2 ring-offset-2 ring-offset-[var(--color-cream-100)] ring-[var(--color-warm-300)]' : ''}
      `}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 ${a.stripe}`} />
      <div className="flex items-start justify-between mb-3">
        {icon && (
          <div className={`w-9 h-9 rounded-[var(--radius-md)] ${a.iconBg} flex items-center justify-center ${a.icon} [&_svg]:w-[18px] [&_svg]:h-[18px]`}>
            {icon}
          </div>
        )}
        {trend && (
          <span className={`text-[11px] font-semibold inline-flex items-center gap-1 ${
            trend.sign === 'up' ? 'text-[var(--color-action-700)]' : 'text-[var(--color-recovery-700)]'
          }`}>
            {trend.sign === 'up' ? '▲' : '▼'} {trend.label}
          </span>
        )}
      </div>
      <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-1.5">
        {label}
      </div>
      <div className="text-[26px] font-extrabold text-[var(--color-warm-900)] tracking-tight tabular-nums leading-none mb-1.5">
        {loading ? <span className="inline-block w-20 h-7 bg-[var(--color-warm-100)] rounded animate-pulse" /> : value}
      </div>
      {sub && (
        <div className="text-[12px] text-[var(--color-warm-600)] leading-snug">
          {sub}
        </div>
      )}
    </Wrapper>
  );
}
